import { ArrowLeft, Star, Heart, Sparkles, BookOpen, Lock, Users } from "lucide-react";

const S = {
  green: "#3E6E64",
  amber: "#C98A2B",
  bg: "#FFF8EC",
  text: "#4A3B2A",
  soft: "#8A7A5E",
};

const Section = ({ icon: Icon, title, children, testId }) => (
  <section className="mb-10" data-testid={testId}>
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "#E8F4EF" }}>
        <Icon className="w-5 h-5" style={{ color: S.green }} />
      </div>
      <h2 className="font-display text-xl sm:text-2xl" style={{ color: S.green }}>{title}</h2>
    </div>
    <div className="text-base leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function TilloAboutPage() {
  return (
    <div className="min-h-screen" style={{ background: S.bg, color: S.text }} data-testid="tillo-about-page">
      <header className="px-6 py-5" style={{ background: "#FFEFD2" }}>
        <div className="max-w-[850px] mx-auto flex items-center justify-between gap-3">
          <a href="/kids" className="flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm transition-transform hover:scale-[1.03]" style={{ background: S.green, color: "#FFFFFF" }} data-testid="tillo-about-back">
            <ArrowLeft className="w-4 h-4" /> Back to Tillo
          </a>
          <div className="text-xs font-semibold uppercase tracking-widest text-right" style={{ color: S.amber }}>Kids · Our Purpose</div>
        </div>
      </header>

      <main className="px-6 py-10 max-w-[850px] mx-auto">
        <div className="flex flex-col sm:flex-row items-center gap-6 mb-10">
          <img src="/tillo.png" alt="Tillo the Storysaur" className="w-40 sm:w-48 drop-shadow-xl" />
          <div className="text-center sm:text-left">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-3" style={{ background: "#FFE3A3", color: "#9A6A14" }}>
              <Star className="w-3.5 h-3.5" /> Our Purpose
            </div>
            <h1 className="font-display text-4xl sm:text-5xl leading-tight" style={{ color: S.green }} data-testid="tillo-about-title">
              Little Stories, Lasting Lessons
            </h1>
          </div>
        </div>

        <Section icon={Heart} title="Why Storylo Kids exists" testId="about-why-exists">
          <p>Storylo was created out of a love of stories — the kind that stay with us, help us understand one another and encourage us to see the world with curiosity and compassion.</p>
          <p>With Storylo Kids, I wanted to share that love with a younger generation. Many of the stories I remember from my own childhood were Aesop's fables. Simple tales about animals and everyday challenges carried lessons about kindness, patience, honesty and perseverance. Those lessons have stayed with me, and they inspired the creation of Tillo the Storysaur.</p>
        </Section>

        <Section icon={Users} title="Meet Tillo and His Friends" testId="about-meet-tillo">
          <p>Tillo is a friendly little dinosaur with a curious mind, a kind heart and a magical golden star on his cheek. Alongside his friends <strong>Toby the tortoise</strong>, <strong>Hoppy the hare</strong>, <strong>Pip the mouse</strong> and <strong>Coco the crow</strong>, he explores a world full of discoveries.</p>
          <p>Sometimes a friend needs encouragement. Sometimes a problem calls for a clever idea. And sometimes Tillo discovers that being gentle can achieve more than pushing.</p>
          <p>Inspired by Aesop's fables, these original adventures bring familiar themes to life through colourful animation, storytelling and original songs. Each story gives children something to enjoy and something to think about, whether it is helping someone smaller than themselves, trying again when something feels difficult or learning to go at their own pace.</p>
        </Section>

        <Section icon={BookOpen} title="More Than a Moral at the End" testId="about-more-than-moral">
          <p>Our purpose is to make stories that children can connect with and families can enjoy together.</p>
          <p>We want to encourage children to ask questions, recognise feelings and discover that their actions matter. Being brave might mean asking for help. Being clever might mean listening to someone else's idea. And making a difference can begin with one small act of kindness.</p>
          <p>Tillo and his friends are still learning, too. They make mistakes, support one another and find their way together — giving parents, grandparents and carers a natural starting point for conversations long after the story has finished.</p>
        </Section>

        <div className="rounded-3xl px-6 py-6 mb-10" style={{ background: "#FFEFD2", border: "3px solid #FFE3A3" }} data-testid="about-personalised-card">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4" style={{ background: S.green, color: "#FFFFFF" }}>
            <Sparkles className="w-3.5 h-3.5" /> Coming soon
          </div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "#FFFFFF" }}>
              <Star className="w-5 h-5" style={{ color: S.amber }} />
            </div>
            <h2 className="font-display text-xl sm:text-2xl" style={{ color: S.green }}>A Place in the Story</h2>
          </div>
          <div className="text-base leading-relaxed space-y-3">
            <p>As these adventures developed, I began to wonder: how lovely would it be for a child to join Tillo and his friends in a story of their own?</p>
            <p>That idea inspired our <strong>personalised video adventures</strong>. Using a cartoon character based on an image supplied by a parent or carer, we can give a child a special place in Tillo's world.</p>
            <p>Whether created for a birthday, a celebration or simply as a thoughtful surprise, each personalised video is intended to be a keepsake — a chance for a child to see themselves taking part in a story filled with friendship, imagination and fun.</p>
          </div>
          <div className="mt-5 rounded-2xl px-5 py-4" style={{ background: "#FFFFFF" }}>
            <div className="flex items-center gap-2 font-bold text-sm mb-2" style={{ color: S.green }}>
              <Lock className="w-4 h-4" /> A Personal Adventure, Shared Privately
            </div>
            <div className="text-sm leading-relaxed space-y-2">
              <p>Personalised videos are a separate, paid-for service, created only when requested. They are intended for private enjoyment and will not be added to Storylo's public story collection.</p>
              <p>The finished video is made available to the customer who ordered it, who can then decide whether to share it with family and friends. A child's personalised adventure will not be used publicly or for promotion without separate permission from their parent or legal guardian.</p>
            </div>
          </div>
        </div>

        <Section icon={Heart} title="Why We Do It" testId="about-why-we-do-it">
          <p>At its heart, Storylo Kids is about sharing something meaningful between generations: the pleasure of a good story, the comfort of familiar characters and the little lessons that help us grow.</p>
          <p>The fables I loved as a child helped inspire this journey. My hope is that Tillo and his friends will create happy memories for children today — and leave them with a little more kindness, confidence and curiosity to carry into tomorrow.</p>
          <p className="font-semibold" style={{ color: S.green }}>Welcome to Tillo's world. There's always another story to discover.</p>
          <p className="italic" style={{ color: S.soft }}>— Aysin, creator of Storylo</p>
        </Section>

        <div className="text-center mt-10">
          <a href="/kids" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-bold text-base transition-transform hover:scale-[1.03]" style={{ background: S.green }} data-testid="about-back-to-adventures">
            <Star className="w-4 h-4" /> Back to Tillo's Adventures
          </a>
        </div>
      </main>

      <footer className="px-6 py-8 text-center text-xs" style={{ color: S.soft }}>
        Tillo the Storysaur · part of <a href="/" className="underline">Storylo</a>
      </footer>
    </div>
  );
}
